import L from 'leaflet';
import { useFleetStore } from '@/stores/fleet';
import { eventBus } from '@/main';
import { NAVAREA_CONFIG } from '@/utils/build-config';

export class NavareaLayer {
    constructor(map, layerControl) {
        this.map = map;
        this.layerControl = layerControl;
        this.overlays = {};
        this.activeNavareaTooltip = null;
        this.navareaTooltipContainer = null;
        this.hoverTooltip = null;
        this.highlightedLayers = [];
        this.navareaElementsMap = new Map();
          
        // Закрыть popup при переключении на другой таб
        eventBus.$on('close-navarea-tooltip', () => {
            this.hideTooltip();
            this.clearHighlight();
        });
        
        // Слушаем событие выделения Navarea объекта на карте
        eventBus.$on('highlight-navarea-on-map', (navareaItem) => {
            this.highlightNavareaElements(navareaItem);
        });
        
        // Слушаем событие сброса выделения
        eventBus.$on('clear-navarea-highlight', () => {
            this.clearHighlight();
        });
    }

    // Добавляем Navarea на карту
    addNavareaLayer() {
        const fleetStore = useFleetStore();

        if (!this.map) return;

        try {
            // Создаем panes для navarea элементов, если они еще не существуют
            if (!this.map.getPane('navarea')) {
                this.map.createPane('navarea');
                this.map.getPane('navarea').style.zIndex = 650;
            }
            
            if (!this.map.getPane('navarea-markers')) {
                this.map.createPane('navarea-markers');
                this.map.getPane('navarea-markers').style.zIndex = 660;
            }

            this.createTooltipContainer();

            // Кластеры маркеров Navarea для ускорения отрисовки
            const navareaClusterGroup = L.markerClusterGroup({
                disableClusteringAtZoom: 16,
                spiderfyOnMaxZoom: true,
                chunkedLoading: true,
                maxClusterRadius: 80,
                showCoverageOnHover: false,
                iconCreateFunction: (cluster) => {
                    return L.divIcon({
                        html: `<div class="cluster-icon" title="⚠️ Navarea warnings: ${cluster.getChildCount()}">${cluster.getChildCount()}</div>`,
                        className: 'navarea-cluster',
                        iconSize: L.point(40, 40)
                    });
                }
            });

            const polygonGroup = L.layerGroup([], {pane: 'navarea'});
            const markers = [];

            let navareaData = fleetStore.data.navarea || [];
            console.log('🗺️ NavArea Layer: Raw navarea data count:', Array.isArray(navareaData) ? navareaData.length : 'not array');

            // Обрабатываем новый формат данных от API
            // Структура: [{id, title, content_full, time_upd, country, type, active_messages: [...], archived_messages: [...]}]
            if (Array.isArray(navareaData) && navareaData.length > 0) {
                console.log('📊 NavArea Layer: Processing NAVAREA data with active_messages...');
                let totalMessages = 0;
                
                navareaData.forEach(navareaItem => {
                    // Для каждого NAVAREA перебираем активные сообщения
                    if (navareaItem.active_messages && Array.isArray(navareaItem.active_messages)) {
                        navareaItem.active_messages.forEach(message => {
                            // Создаем объект в формате, совместимом с существующим кодом
                            const entry = {
                                id: message.id,
                                number: message.number,
                                content: message.content,
                                country: navareaItem.country,
                                type: navareaItem.type,
                                time_upd: navareaItem.time_upd,
                                // Координаты УЖЕ есть в message.decimal_coordinates!
                                coordinates: {
                                    decimal_coordinates: message.decimal_coordinates || [],
                                    geojson_coordinates: [] // GeoJSON будет создана из decimal если нужна
                                }
                            };
                            
                            this.processMessageCoordinates(entry, markers, polygonGroup);
                            totalMessages++;
                        });
                    }
                });
                
                console.log(`✅ NavArea Layer: Processed ${navareaData.length} NAVAREA regions with ${totalMessages} active messages, markers count: ${markers.length}`);
            } else {
                console.log('❌ NavArea Layer: No valid navarea data found');
            }

            navareaData = null

            if (!this._boundHandleMapClick) {
                this._boundHandleMapClick = this.handleMapClick.bind(this);
            }
            this.map.off('click', this._boundHandleMapClick);
            this.map.on('click', this._boundHandleMapClick);

            navareaClusterGroup.addLayers(markers);

            this.overlays.navarea = L.layerGroup([navareaClusterGroup, polygonGroup]);

            // Добавляем слой Navarea на карту
            if (this.layerControl) {
                this.layerControl.addOverlay(this.overlays.navarea, 'Navarea Warnings');
            }
            
            return this.overlays.navarea;
        } catch (error) {
            console.error('Failed to load Navarea data:', error);
        }
    }

    // Создание контейнера для тултипа Navarea
    createTooltipContainer() {
        if (this.navareaTooltipContainer) return;
        
        this.navareaTooltipContainer = document.createElement('div');
        this.navareaTooltipContainer.className = 'navarea-tooltip';
        this.navareaTooltipContainer.style.position = 'absolute';
        this.navareaTooltipContainer.style.display = 'none';
        this.navareaTooltipContainer.style.backgroundColor = '#ffffff';
        this.navareaTooltipContainer.style.border = '1px solid #ddd';
        this.navareaTooltipContainer.style.borderRadius = '8px';
        this.navareaTooltipContainer.style.padding = '0';
        this.navareaTooltipContainer.style.maxWidth = '450px';
        this.navareaTooltipContainer.style.minWidth = '320px';
        this.navareaTooltipContainer.style.maxHeight = '70vh';
        this.navareaTooltipContainer.style.overflowY = 'auto';
        this.navareaTooltipContainer.style.fontSize = '14px';
        this.navareaTooltipContainer.style.lineHeight = '1.5';
        this.navareaTooltipContainer.style.zIndex = '10000';
        this.navareaTooltipContainer.style.pointerEvents = 'auto';
        this.navareaTooltipContainer.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
        this.navareaTooltipContainer.style.fontFamily = 'Arial, sans-serif';
        
        const mapContainer = this.map.getContainer();
        mapContainer.appendChild(this.navareaTooltipContainer);
    }

    // Форматирование содержимого тултипа с цветными бэджами
    formatTooltipContent(entry) {
        // Форматирование времени из ISO в читаемый формат
        let formattedTime = 'N/A';
        if (entry.time_upd) {
            try {
                const date = new Date(entry.time_upd);
                const options = {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'UTC'
                };
                formattedTime = date.toLocaleDateString('en-US', options) + ' UTC';
            } catch (error) {
                console.warn('Error formatting time:', error);
                formattedTime = entry.time_upd;
            }
        }

        // Форматирование содержимого с учетом переводов строк
        const formattedContent = entry.content ? 
            entry.content.replace(/\\r\\n|\\n|\\r|\r\n|\n|\r/g, '<br>') : 'No content available';

        // Создание HTML в стиле AIO popup
        const html = `
            <div style="position: sticky; top: 0; background: white; z-index: 1; padding: 20px 20px 15px 20px; border-radius: 8px 8px 0 0;">
                <h3 style="margin: 0 0 12px 0; font-size: 16px; font-weight: 600; color: #333; line-height: 1.3;">
                    NAVAREA ${entry.number || 'N/A'}
                </h3>
                <div style="margin-bottom: 10px;">
                    <span style="display: inline-block; background: #f5f5f5; color: #666; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 500; margin-right: 8px; border: 1px solid #e0e0e0;">
                        🌍 ${entry.country || 'N/A'}
                    </span>
                    <span style="display: inline-block; background: #f5f5f5; color: #666; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 500; border: 1px solid #e0e0e0;">
                        🕒 ${formattedTime}
                    </span>
                </div>
            </div>
            <div style="padding: 0 20px 20px 20px; color: #444; line-height: 1.6; font-size: 12px; ">
                ${formattedContent}
            </div>
        `;

        return html;
    }

    // Обработка координат сообщений
    processMessageCoordinates(entry, markers, polygonGroup) {
        // console.log('🔧 Processing coordinates for entry:', entry.number || entry.id1, entry.coordinates);
        
        if (!entry.coordinates) {
            console.log('❌ No coordinates found for entry:', entry.number || entry.id1);
            return;
        }

        const entryMarkers = [];
        const entryPolygons = [];

        // Получаем координаты из доступных форматов
        let coords = null;
        if (entry.coordinates.decimal_coordinates) {
            coords = entry.coordinates.decimal_coordinates;
            // console.log('📍 Using decimal_coordinates:', coords);
        } else if (entry.coordinates.geojson_coordinates) {
            coords = entry.coordinates.geojson_coordinates;
            // console.log('📍 Using geojson_coordinates:', coords);
        }
        
        if (!coords) {
            console.log('❌ No valid coordinates format found for entry:', entry.number || entry.id1);
            return;
        }

        if (!coords) return;

        // Обрабатываем разные форматы координат
        if (Array.isArray(coords)) {
            // Если массив координат
            if (coords.length === 1) {
                // Одна точка - создаем маркер
                this.createMarkerFromCoords(coords[0], entry, markers, entryMarkers);
            } else if (coords.length > 1) {
                // Несколько точек - выбираем режим отображения
                if (NAVAREA_CONFIG.DISPLAY_POLYGONS_AS_POINTS || coords.length > NAVAREA_CONFIG.MAX_POLYGON_POINTS) {
                    // Режим отображения как отдельные точки
                    console.log(`🔄 Converting polygon to ${coords.length} separate points (safe mode)`);
                    coords.forEach((coord, index) => {
                        // Создаем отдельный маркер для каждой точки с дополнительной информацией
                        const pointEntry = {
                            ...entry,
                            number: `${entry.number} (point ${index + 1})`,
                            content: `${entry.content}\n\n[Point ${index + 1} of ${coords.length}]`
                        };
                        this.createMarkerFromCoords(coord, pointEntry, markers, entryMarkers);
                    });
                } else {
                    // Стандартный режим - создаем полигон или полилинию
                    console.log(`📐 Creating polygon/polyline with ${coords.length} points`);
                    this.createPolygonFromCoords(coords, entry, polygonGroup, entryPolygons);
                }
            }
        } else if (coords.lat !== undefined && coords.lon !== undefined) {
            // Объект с lat/lon - создаем маркер
            this.createMarkerFromCoords(coords, entry, markers, entryMarkers);
        }

        // Сохраняем элементы для данного объекта
        if (entryMarkers.length > 0 || entryPolygons.length > 0) {
            this.navareaElementsMap.set(entry.number, {
                markers: entryMarkers,
                polygons: entryPolygons
            });
        }
    }

    // Создание маркера из координат
    createMarkerFromCoords(coords, entry, markers, entryMarkers) {
        let lat, lon;
        
        if (coords.lat !== undefined && coords.lon !== undefined) {
            lat = parseFloat(coords.lat);
            lon = parseFloat(coords.lon);
            console.log(`📍 Object coords for ${entry.number}: lat=${lat}, lon=${lon}`);
        } else if (Array.isArray(coords) && coords.length >= 2) {
            // ВАЖНО: Проверяем, что именно приходит в массиве
            // console.log(`📊 Array coords for ${entry.number}:`, coords);
            
            // GeoJSON формат: [longitude, latitude]
            // Но иногда может быть [latitude, longitude]
            // Проверяем разумность координат
            const first = parseFloat(coords[0]);
            const second = parseFloat(coords[1]);
            
            // Широта должна быть в диапазоне -90...90
            // Долгота должна быть в диапазоне -180...180
            if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
                // Вероятно формат [lat, lon]
                lat = first;
                lon = second;
                // console.log(`🔄 Assuming [lat, lon] format: lat=${lat}, lon=${lon}`);
            } else {
                // Стандартный GeoJSON формат [lon, lat]
                lat = second;
                lon = first;
                // console.log(`📋 Using GeoJSON [lon, lat] format: lat=${lat}, lon=${lon}`);
            }
        } else {
            return;
        }

        if (isNaN(lat) || isNaN(lon)) {
            // console.error(`❌ Invalid coordinates for ${entry.number}: lat=${lat}, lon=${lon}`);
            return;
        }

        // Дополнительная проверка на разумность координат
        if (Math.abs(lat) > 90 || Math.abs(lon) > 180) {
            // console.error(`❌ Coordinates out of range for ${entry.number}: lat=${lat}, lon=${lon}`);
            return;
        }

        const marker = L.circleMarker([lat, lon], {
            radius: 8,
            fillColor: '#f30d5f',
            color: '#ffffff',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
            pane: 'navarea-markers'
        });

        marker.on('click', e => {
            L.DomEvent.stopPropagation(e);
            this.handleNavareaClick(entry);
        });

        marker.bindTooltip(`${entry.number}`, {
            permanent: false,
            direction: 'top',
            offset: [0, -10],
            className: 'navarea-tooltip-mini'
        });

        marker._originalOptions = {
            radius: 8,
            fillColor: '#f30d5f',
            color: '#ffffff',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8
        };

        marker._navareaId = entry.number;
        markers.push(marker);
        entryMarkers.push(marker);
    }

    // Создание полигона из координат
    createPolygonFromCoords(coords, entry, polygonGroup, entryPolygons) {
        const latLngs = [];
        
        coords.forEach((coord, index) => {
            let lat, lon;
            
            if (coord.lat !== undefined && coord.lon !== undefined) {
                lat = parseFloat(coord.lat);
                lon = parseFloat(coord.lon);
            } else if (Array.isArray(coord) && coord.length >= 2) {
                console.log(`📊 Polygon coord ${index} for ${entry.number}:`, coord);
                
                const first = parseFloat(coord[0]);
                const second = parseFloat(coord[1]);
                
                // Определяем формат координат
                if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
                    // Вероятно формат [lat, lon]
                    lat = first;
                    lon = second;
                } else {
                    // Стандартный GeoJSON формат [lon, lat]
                    lat = second;
                    lon = first;
                }
            } else {
                return;
            }

            if (!isNaN(lat) && !isNaN(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
                latLngs.push([lat, lon]);
            } else {
                console.warn(`⚠️ Invalid polygon coordinate for ${entry.number}:`, coord);
            }
        });

        if (latLngs.length < 2) return;

        // Проверяем на потенциальные проблемы с полигоном
        if (this.hasPolygonIssues(latLngs)) {
            console.warn(`⚠️ Polygon for ${entry.number} may have issues (self-intersections or complex geometry), consider using point mode`);
        }

        const polygon = L.polygon(latLngs, {
            color: '#f30d5f',
            weight: 2,
            opacity: 0.6,
            fillOpacity: 0.2,
            pane: 'navarea'
        });

        polygon.on('click', e => {
            L.DomEvent.stopPropagation(e);
            this.handleNavareaClick(entry);
        });

        polygon.bindTooltip(`${entry.number}`, {
            permanent: false,
            direction: 'top',
            offset: [0, -10],
            className: 'navarea-tooltip-mini'
        });
        
        polygon._originalOptions = {
            color: '#f30d5f',
            weight: 2,
            opacity: 0.6,
            fillOpacity: 0.2
        };
        
        polygon._navareaId = entry.number;
        polygonGroup.addLayer(polygon);
        entryPolygons.push(polygon);
    }
    
    // Показываем или скрываем тултип Navarea при клике на маркер или полигон
    handleNavareaClick(entry) {
        // Эмитим событие для выделения объекта в таблице
        eventBus.$emit('navarea-item-selected-on-map', {
            id: entry.id,
            number: entry.number,
            content: entry.content,
            country: entry.country,
            type: entry.type,
            data: entry
        });
        
        // Эмитим дополнительное событие для выделения в таблице
        eventBus.$emit('highlight-navarea-item-in-table', {
            id: entry.id,
            number: entry.number,
            content: entry.content,
            country: entry.country
        });
        
        if (this.activeNavareaTooltip === entry.number) {
            this.hideTooltip();
        } else {
            console.log(entry);
            const content = this.formatTooltipContent(entry);
            this.showTooltip(content);
            this.activeNavareaTooltip = entry.number;
        }
    }

    // Отображаем тултип с информацией
    showTooltip(content) {
        if (!this.navareaTooltipContainer) return;
        
        this.navareaTooltipContainer.innerHTML = content;
        
        // Добавляем кнопку закрытия в стиле AIO
        const closeButton = document.createElement('div');
        closeButton.className = 'navarea-tooltip-close';
        closeButton.innerHTML = '✕';
        closeButton.style.cssText = `
            position: absolute;
            top: 15px;
            right: 15px;
            width: 24px;
            height: 24px;
            background: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 12px;
            color: #666;
            font-weight: bold;
            z-index: 10;
            transition: all 0.2s ease;
        `;
        
        // Добавляем hover эффекты
        closeButton.addEventListener('mouseenter', () => {
            closeButton.style.background = '#e0e0e0';
            closeButton.style.color = '#333';
        });
        
        closeButton.addEventListener('mouseleave', () => {
            closeButton.style.background = '#f5f5f5';
            closeButton.style.color = '#666';
        });
        
        closeButton.addEventListener('click', () => this.hideTooltip());
        this.navareaTooltipContainer.appendChild(closeButton);
        
        this.navareaTooltipContainer.style.display = 'block';
        
        // Позиционируем тултип в центре карты с учетом новой ширины
        const mapSize = this.map.getSize();
        this.navareaTooltipContainer.style.left = (mapSize.x / 2 - 225) + 'px';
        this.navareaTooltipContainer.style.top = '50px';
    }

    // Скрываем тултип
    hideTooltip() {
        if (this.navareaTooltipContainer) {
            this.navareaTooltipContainer.style.display = 'none';
        }
        this.activeNavareaTooltip = null;
    }

    // Отключаем обработчики событий карты при наведении на тултип
    disableMapHandlers() {
        if (this.map) {
            this.map.dragging.disable();
            this.map.touchZoom.disable();
            this.map.doubleClickZoom.disable();
            this.map.scrollWheelZoom.disable();
            this.map.boxZoom.disable();
            this.map.keyboard.disable();
        }
    }

    // Включаем обработчики событий карты при уходе с тултипа
    enableMapHandlers() {
        if (this.map) {
            this.map.dragging.enable();
            this.map.touchZoom.enable();
            this.map.doubleClickZoom.enable();
            this.map.scrollWheelZoom.enable();
            this.map.boxZoom.enable();
            this.map.keyboard.enable();
        }
    }

    // Закрыть всплывающую подсказку при клике на карте
    handleMapClick() {
        this.hideTooltip();
    }
    
    // Выделяем элементы Navarea объекта на карте
    highlightNavareaElements(navareaItem) {
        // Сначала сбрасываем предыдущее выделение
        this.clearHighlight();
        
        const itemId = navareaItem.number || navareaItem.id;
        if (!itemId) return;
        
        const elements = this.navareaElementsMap.get(itemId);
        if (!elements) return;
        
        // Выделяем маркеры
        elements.markers.forEach(marker => {
            marker.setStyle({
                color: '#ff6b00',
                fillColor: '#ff6b00',
                opacity: 1.0,
                fillOpacity: 0.8,
                weight: 4,
                radius: 10
            });
            this.highlightedLayers.push(marker);
        });
        
        // Выделяем полигоны
        elements.polygons.forEach(polygon => {
            if (polygon.setStyle) {
                polygon.setStyle({
                    color: '#ff6b00',
                    weight: 4,
                    opacity: 1.0,
                    fillOpacity: 0.4
                });
            } else {
                polygon.setStyle({
                    color: '#ff6b00',
                    fillColor: '#ff6b00',
                    opacity: 1.0,
                    fillOpacity: 0.8,
                    weight: 4,
                    radius: 10
                });
            }
            this.highlightedLayers.push(polygon);
        });
    }
    
    // Сбрасываем выделение всех элементов
    clearHighlight() {
        this.highlightedLayers.forEach(layer => {
            if (layer._originalOptions) {
                layer.setStyle(layer._originalOptions);
            }
        });
        this.highlightedLayers = [];
    }

    // Удаляем слой Navarea плюс очистка
    destroy() {
        try {
            // Сбрасываем выделение
            this.clearHighlight();
            
            // Очищаем карту элементов с принудительным освобождением памяти
            if (this.navareaElementsMap) {
                this.navareaElementsMap.forEach((elements) => {
                    if (Array.isArray(elements)) {
                        elements.forEach(element => {
                            if (element && element.remove) {
                                element.remove();
                            }
                        });
                    }
                });
                this.navareaElementsMap.clear();
                this.navareaElementsMap = null;
            }
            
            // Очищаем выделенные слои
            if (this.highlightedLayers) {
                this.highlightedLayers.forEach(layer => {
                    if (layer && layer.remove) {
                        layer.remove();
                    }
                });
                this.highlightedLayers = null;
            }
            
            // Удаляем тултип Navarea, если он существует
            if (this.navareaTooltipContainer) {
                if (this.navareaTooltipContainer.parentNode) {
                    this.navareaTooltipContainer.parentNode.removeChild(this.navareaTooltipContainer);
                }
                this.navareaTooltipContainer = null;
            }

            // Удаляем слои с принудительной очисткой
            if (this.overlays && this.overlays.navarea) {
                if (this.map && this.map.hasLayer(this.overlays.navarea)) {
                    this.map.removeLayer(this.overlays.navarea);
                }
                
                if (this.overlays.navarea.clearLayers) {
                    this.overlays.navarea.clearLayers();
                }
                
                this.overlays.navarea = null;
            }
            
            // Очищаем все overlays
            if (this.overlays) {
                Object.keys(this.overlays).forEach(key => {
                    const layer = this.overlays[key];
                    if (layer && this.map && this.map.hasLayer(layer)) {
                        this.map.removeLayer(layer);
                    }
                    if (layer && layer.clearLayers) {
                        layer.clearLayers();
                    }
                });
                this.overlays = {};
            }

            // Удаляем обработчик клика на карте
            if (this.map && this._boundHandleMapClick) {
                this.map.off('click', this._boundHandleMapClick);
                this._boundHandleMapClick = null;
            }
            
            // Удаляем обработчики событий
            if (typeof eventBus !== 'undefined' && eventBus) {
                eventBus.$off('close-navarea-tooltip');
                eventBus.$off('highlight-navarea-on-map');
                eventBus.$off('clear-navarea-highlight');
            }
            
            // Обнуляем ссылки
            this.map = null;
            this.layerControl = null;
            
        } catch (error) {
            console.warn('Ошибка при очистке Navarea layer:', error);
            
            // В случае ошибки все равно обнуляем основные ссылки
            this.navareaElementsMap = null;
            this.highlightedLayers = null;
            this.navareaTooltipContainer = null;
            this.overlays = {};
            this.map = null;
            this.layerControl = null;
        }
    }

    // Проверка на потенциальные проблемы с полигонами
    hasPolygonIssues(latLngs) {
        if (latLngs.length < 3) return false;

        // Простая проверка на очень близкие точки (возможные дубликаты)
        for (let i = 0; i < latLngs.length; i++) {
            for (let j = i + 1; j < latLngs.length; j++) {
                const dist = Math.sqrt(
                    Math.pow(latLngs[i][0] - latLngs[j][0], 2) + 
                    Math.pow(latLngs[i][1] - latLngs[j][1], 2)
                );
                // Если точки очень близко друг к другу (меньше 0.001 градуса ≈ 100м)
                if (dist < 0.001) {
                    return true;
                }
            }
        }

        // Проверка на нерегулярные прыжки в координатах (возможные ошибки данных)
        for (let i = 1; i < latLngs.length; i++) {
            const dist = Math.sqrt(
                Math.pow(latLngs[i][0] - latLngs[i-1][0], 2) + 
                Math.pow(latLngs[i][1] - latLngs[i-1][1], 2)
            );
            // Если расстояние между соседними точками больше 1 градуса (≈ 100км)
            if (dist > 1) {
                return true;
            }
        }

        return false;
    }
}